import React, { useState } from 'react';
import { useLicense } from '../contexts/LicenseContext';
import { useCapabilities } from '../contexts/CapabilitiesContext';

interface LicenseStatusProps {
  className?: string;
}

const LicenseStatus: React.FC<LicenseStatusProps> = ({ className = '' }) => {
  const { licenseInfo, loading, error, refresh } = useLicense();
  const { capabilities } = useCapabilities();
  const [expanded, setExpanded] = useState(false);

  if (loading && !licenseInfo) {
    return (
      <div className={`bg-neutral-50 border border-neutral-200 rounded-lg p-4 ${className}`}>
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-neutral-300 rounded w-3/4"></div>
            <div className="h-4 bg-neutral-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // A guest has no licence, so licence data describes them wrongly rather than
  // incompletely: /api/license-info answers NO_LICENSE and 0 / 0 while
  // /api/capabilities says five uploads are available. Two sources of truth
  // disagreeing on one screen, and the discouraging one was winning - it told
  // a visitor they could do nothing at the exact moment they could.
  //
  // Keyed on identity, not on tier. "Is this a guest?" is a question about who
  // is here, which Identity exists to answer; the tier-name comparisons are
  // what this file must not add another of.
  if (capabilities?.identity === 'ANONYMOUS') {
    const expires = capabilities.sessionExpiresAt
      ? new Date(capabilities.sessionExpiresAt).toLocaleDateString(undefined,
          { month: 'short', day: 'numeric' })
      : null;

    return (
      <div className={`bg-white border border-neutral-200 rounded-lg ${className}`}>
        <div className="px-4 py-3 flex items-center space-x-3 border-b border-neutral-100">
          <svg className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-sm text-neutral-900">Your session</span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full border border-primary-200 bg-primary-50 text-primary-700">
                Guest
              </span>
            </div>
            {expires && <p className="text-xs text-neutral-500">Saved here until {expires}</p>}
          </div>
        </div>

        <div className="px-4 py-3 space-y-2.5">
          {/* Counts what is LEFT, not "n of m". The wire carries the remainder
              and deliberately not the allowance - a capability answers "may I",
              and inventing a denominator here would be inventing a fact. */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-700">PDF uploads</span>
            <span className="text-xs font-semibold text-neutral-900">
              {capabilities.uploadsRemaining} remaining
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-700">Build &amp; preview</span>
            <span className="text-xs font-semibold text-success-600">Included</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-700">Downloads</span>
            <span className="text-xs font-medium text-neutral-500">With an account</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !licenseInfo) {
    return (
      <div className={`bg-neutral-100 border border-neutral-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center text-neutral-500 text-sm">
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
          </svg>
          <span className="font-medium">Offline</span>
          <button
            onClick={refresh}
            className="ml-auto text-xs text-primary-600 hover:text-primary-700 font-medium"
            title="Retry connection"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getTierBadgeColor = (tier: string) => {
    // 4-tier model: Trial / Professional / Business / Enterprise
    switch (tier.toUpperCase()) {
      case 'TRIAL':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'PROFESSIONAL':
        return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'BUSINESS':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'ENTERPRISE':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    }
  };

  const getUsageColor = (percentUsed: number) => {
    if (percentUsed >= 90) return 'bg-red-500';
    if (percentUsed >= 70) return 'bg-orange-500';
    if (percentUsed >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const pdfUploadsPercent = licenseInfo.pdfUploads.percentUsed
    ?? (licenseInfo.pdfUploads.limit > 0
      ? (licenseInfo.pdfUploads.current / licenseInfo.pdfUploads.limit) * 100
      : 0);

  // Flattened PDFs draw down from TWO places and the allowance pays for both:
  // PDFs this instance flattened, plus PDFs reserved by generated projects,
  // which spend their budget independently and are never seen here. Showing
  // only `current` made a project allocation look free.
  const flattenedUsed =
    licenseInfo.flattenedPdfs.current + (licenseInfo.flattenedPdfs.allocated ?? 0);

  // Projects still available this period. Clamped at 0 so a mid-period tier
  // downgrade (current > limit) reads "0 ... remaining" rather than a negative.
  const projectsRemaining = Math.max(
    0,
    licenseInfo.codeGeneration.limit - licenseInfo.codeGeneration.current
  );

  // periodType is an enum name (MONTHLY/YEARLY), not a noun - lowercasing it
  // produced "projects per monthly". Map it to a phrase instead.
  const periodPhrase = ((): string => {
    switch (licenseInfo.periodType?.toUpperCase()) {
      case 'MONTHLY': return 'this month';
      case 'YEARLY': return 'this year';
      case 'TRIAL': return 'during this trial';
      default: return 'this period';
    }
  })();

  return (
    <div className={`bg-white border border-neutral-200 rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center space-x-3">
          <svg className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-sm text-neutral-900">License Status</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getTierBadgeColor(licenseInfo.tier)}`}>
                {licenseInfo.tier}
              </span>
            </div>
            <p className="text-xs text-neutral-500">{licenseInfo.periodType}</p>
          </div>
        </div>
        <svg
          className={`h-5 w-5 text-neutral-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-neutral-100">
          {/* License details (customer + expiration) */}
          {(licenseInfo.customerName || licenseInfo.expiresAt) && (
            <div className="pt-3 space-y-1">
              {licenseInfo.customerName && (
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-500">Customer</span>
                  <span className="font-medium text-neutral-800 truncate ml-2" title={licenseInfo.customerName}>
                    {licenseInfo.customerName}
                  </span>
                </div>
              )}
              {licenseInfo.expiresAt && (
                <div className="flex justify-between text-xs">
                  <span className="text-neutral-500">Expires</span>
                  <span className="font-medium text-neutral-800">
                    {new Date(licenseInfo.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* PDF Uploads */}
          <div className="pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-neutral-700">PDF Uploads</span>
              <span className="text-xs text-neutral-500">
                {`${licenseInfo.pdfUploads.current} / ${licenseInfo.pdfUploads.limit}`}
              </span>
            </div>
            {licenseInfo.pdfUploads.limit > 0 && (
              <div className="w-full bg-neutral-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getUsageColor(pdfUploadsPercent)}`}
                  style={{ width: `${Math.min(pdfUploadsPercent, 100)}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* Code Generation */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-neutral-700">Code Generation</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                licenseInfo.codeGeneration.enabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {licenseInfo.codeGeneration.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {licenseInfo.codeGeneration.enabled && (
              <p className="text-xs text-neutral-500">
                {licenseInfo.codeGeneration.limit > 0
                  ? `${projectsRemaining} of ${licenseInfo.codeGeneration.limit} ${licenseInfo.codeGeneration.limit === 1 ? 'project' : 'projects'} remaining ${periodPhrase}`
                  : `No projects included ${periodPhrase}`
                }
              </p>
            )}
          </div>

          {/* Flattened PDFs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-neutral-700">Flattened PDFs</span>
              <span className="text-xs text-neutral-500">
                {`${flattenedUsed} / ${licenseInfo.flattenedPdfs.limit}`}
              </span>
            </div>
            {licenseInfo.flattenedPdfs.limit > 0 && (
              <>
                <div className="w-full bg-neutral-200 rounded-full h-2 mb-1">
                  <div
                    className={`h-2 rounded-full transition-all ${getUsageColor(
                      (flattenedUsed / licenseInfo.flattenedPdfs.limit) * 100
                    )}`}
                    style={{ width: `${Math.min((flattenedUsed / licenseInfo.flattenedPdfs.limit) * 100, 100)}%` }}
                  ></div>
                </div>
                {(licenseInfo.flattenedPdfs.allocated ?? 0) > 0 && (
                  <p className="text-xs text-neutral-400">
                    {licenseInfo.flattenedPdfs.allocated?.toLocaleString()} allocated to projects
                  </p>
                )}
              </>
            )}
          </div>

          {/* Refresh Button */}
          <div className="pt-2 border-t border-neutral-100">
            <button
              onClick={refresh}
              className="w-full text-xs text-primary-600 hover:text-primary-700 font-medium py-1 hover:bg-primary-50 rounded transition-colors"
            >
              Refresh Status
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseStatus;
